/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import type { ReviewStatisticResponse, ReviewStatistics } from '../interface/types';
import type { ReviewStatistic } from '../../customReviews/interface/types';
import { isRetryableError } from '@/features/customReviews/utils/retryStrategies';

const URL = `${requestBaseURLGCPExtOrInt}/sls/product/product-reviews-integration-bs/v1/review-statistics`;

type GetReviewStatisticsOptions = {
  skuNumbers: string[];
};

type GetSingleReviewStatisticOptions = {
  skuNumber: string;
};

const singleSelector = (statistics: ReviewStatisticResponse[]): ReviewStatistic => {
  if (!statistics || !statistics.length) {
    return {
      skuNumber: '',
      averageOverallRating: '0',
      totalReviewCount: '0',
      recommendedCount: '0',
      recommendPercentage: '0',
      notRecommendedCount: '0',
      ratingDistributionValues: [],
    };
  }

  const statistic = statistics[0];
  const recommendPercentage = statistic.recommendPercentage
    ? parseFloat(statistic.recommendPercentage)
    : 0;
  const recommendedCount = statistic.recommendedCount ? parseFloat(statistic.recommendedCount) : 0;
  const totalReviewCount = statistic.totalReviewCount ? parseFloat(statistic.totalReviewCount) : 0;

  return {
    ...statistic,
    recommendPercentage: recommendPercentage.toString(),
    recommendedCount: recommendedCount.toString(),
    totalReviewCount: totalReviewCount.toString(),
  };
};

const getSingleReviewStatistic = async (
  options: GetSingleReviewStatisticOptions,
  axiosInstance?: AxiosInstance
) => {
  const { skuNumber } = options;
  const response = await getAxios(axiosInstance).get<ReviewStatisticResponse[]>(
    `${URL}?skuNumbers=${skuNumber}`,
    {
      withCredentials: false,
    }
  );
  return singleSelector(response.data);
};

const selector = (statistics: ReviewStatisticResponse[]) => {
  const reviewStatistics: ReviewStatistics = {};
  statistics.forEach((reviewStatistic) => {
    reviewStatistics[reviewStatistic.skuNumber] = singleSelector([reviewStatistic]);
  });

  return reviewStatistics;
};

const getReviewStatistics = async (
  options: GetReviewStatisticsOptions,
  axiosInstance?: AxiosInstance
) => {
  const { skuNumbers } = options;

  if (!skuNumbers.length) {
    return {};
  }

  const response = await getAxios(axiosInstance).get<ReviewStatisticResponse[]>(
    `${URL}?skuNumbers=${skuNumbers.join(',')}`,
    {
      withCredentials: false,
    }
  );

  return selector(response.data);
};

const { useData: useReviewStatisticsData, prefetch: prefetchReviewStatisticsData } = createQuery<
  ReviewStatistics,
  GetReviewStatisticsOptions
>('reviewStatistics', async (options, axiosInstance) =>
  getReviewStatistics({ ...options }, axiosInstance)
);

const { useData: useSingleReviewStatisticData, prefetch: prefetchSingleReviewStatisticData } =
  createQuery<ReviewStatistic, GetSingleReviewStatisticOptions>(
    'singleReviewStatistic',
    async (options, axiosInstance) => getSingleReviewStatistic({ ...options }, axiosInstance)
  );

type ReviewStatisticsOptions = GetReviewStatisticsOptions & {
  enabled?: boolean;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
};

export const useReviewStatistics = (options: ReviewStatisticsOptions) => {
  const { enabled = true } = options;
  return useReviewStatisticsData({
    ...options,
    enabled,
    retry: isRetryableError,
    retryDelay: 1000,
  });
};

type SingleReviewStatisticOptions = GetSingleReviewStatisticOptions & {
  enabled?: boolean;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
};

export const useSingleReviewStatistic = (options: SingleReviewStatisticOptions) => {
  const { enabled = true } = options;
  return useSingleReviewStatisticData({
    ...options,
    enabled,
    retry: isRetryableError,
    retryDelay: 1000,
  });
};

export const prefetchReviewStatistics = (
  options: GetReviewStatisticsOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchReviewStatisticsData(queryClient, options, axiosInstance);
};

export const prefetchSingleReviewStatistic = (
  options: GetSingleReviewStatisticOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchSingleReviewStatisticData(queryClient, options, axiosInstance);
};
